import React, { useState, useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { adminTeammateFinderAPI } from '../services/adminApi';

const AdminTeammateFinderSection = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [filterCanton, setFilterCanton] = useState('');
  const [filterPublished, setFilterPublished] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isFeatureEnabled, setIsFeatureEnabled] = useState(true);
  const [loadingSetting, setLoadingSetting] = useState(true);
  const [savingSetting, setSavingSetting] = useState(false);
  const queryClient = useQueryClient();

  const cantons = [
    { code: 'AG', name: 'Aargau' }, { code: 'AI', name: 'Appenzell Innerrhoden' },
    { code: 'AR', name: 'Appenzell Ausserrhoden' }, { code: 'BE', name: 'Bern' },
    { code: 'BL', name: 'Basel-Landschaft' }, { code: 'BS', name: 'Basel-Stadt' },
    { code: 'FR', name: 'Fribourg' }, { code: 'GE', name: 'Geneva' },
    { code: 'GL', name: 'Glarus' }, { code: 'GR', name: 'Graubünden' },
    { code: 'JU', name: 'Jura' }, { code: 'LU', name: 'Lucerne' },
    { code: 'NE', name: 'Neuchâtel' }, { code: 'NW', name: 'Nidwalden' },
    { code: 'OW', name: 'Obwalden' }, { code: 'SG', name: 'St. Gallen' },
    { code: 'SH', name: 'Schaffhausen' }, { code: 'SO', name: 'Solothurn' },
    { code: 'SZ', name: 'Schwyz' }, { code: 'TG', name: 'Thurgau' },
    { code: 'TI', name: 'Ticino' }, { code: 'UR', name: 'Uri' },
    { code: 'VD', name: 'Vaud' }, { code: 'VS', name: 'Valais' },
    { code: 'ZG', name: 'Zug' }, { code: 'ZH', name: 'Zurich' }
  ];

  const skillLevels = [
    { value: '1.0-2.5', label: '1.0 – 2.5 : Beginner' },
    { value: '3.0-3.5', label: '3.0 – 3.5 : Beginner +' },
    { value: '4.0-4.5', label: '4.0 – 4.5 : Intermediate' },
    { value: '5.0-5.5', label: '5.0 – 5.5 : Intermediate +' },
    { value: '6.0-6.5', label: '6.0 – 6.5 : Advanced' },
    { value: '7.0-7.5', label: '7.0 – 7.5 : Advanced +' },
    { value: '8.0-10.0', label: '8.0 – 10.0 : Elite' }
  ];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchFeatureSetting();
    fetchProfiles();
  }, [filterCanton, filterPublished]);
  // Note: fetchFeatureSetting and fetchProfiles intentionally excluded - they're recreated on each render and would cause infinite loops

  const fetchFeatureSetting = async () => {
    try {
      setLoadingSetting(true);
      const response = await adminTeammateFinderAPI.getFeatureSetting();
      if (response.success) {
        setIsFeatureEnabled(response.data?.enabled !== false); // Default to true if not set
      }
    } catch (error) {
      console.error('Error fetching feature setting:', error);
      // Default to enabled if error
      setIsFeatureEnabled(true);
    } finally {
      setLoadingSetting(false);
    }
  };

  const handleToggleFeature = async (enabled) => {
    try {
      setSavingSetting(true);
      console.log('🔄 Toggling feature setting:', enabled, typeof enabled);
      const response = await adminTeammateFinderAPI.updateFeatureSetting(enabled);
      console.log('📥 Feature setting response:', response);
      
      if (response && response.success) {
        setIsFeatureEnabled(enabled);
        console.log('✅ Feature setting updated successfully');
      } else {
        const errorMsg = response?.message || 'Failed to update setting';
        console.error('❌ Feature setting update failed:', response);
        alert(errorMsg);
      }
    } catch (error) {
      console.error('❌ Error updating feature setting:', error);
      console.error('❌ Error response:', error.response);
      console.error('❌ Error data:', error.response?.data);
      
      // Show the actual error message from the server
      const errorMessage = error.response?.data?.message 
        || error.response?.data?.error 
        || error.message 
        || 'Failed to update setting';
      
      alert(`Error: ${errorMessage}`);
    } finally {
      setSavingSetting(false);
    }
  };

  const fetchProfiles = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (filterCanton) filters.canton = filterCanton;
      if (filterPublished !== '') filters.published = filterPublished;
      const response = await adminTeammateFinderAPI.getAllProfiles(filters);
      if (response.success) {
        setProfiles(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching profiles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (profile) => {
    setEditingId(profile.id);
    setEditForm({
      firstName: profile.first_name,
      lastName: profile.last_name,
      email: profile.email,
      phoneNumber: profile.phone_number,
      canton: profile.canton,
      skillLevel: profile.skill_level || '',
      isPublished: profile.is_published
    });
  };

  const handleSaveEdit = async () => {
    try {
      const response = await adminTeammateFinderAPI.updateProfile(editingId, {
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        email: editForm.email,
        phoneNumber: editForm.phoneNumber,
        canton: editForm.canton,
        skillLevel: editForm.skillLevel || null,
        isPublished: editForm.isPublished
      });
      if (response.success) {
        setEditingId(null);
        setEditForm({});
        fetchProfiles();
        queryClient.invalidateQueries(['adminTeammateFinder']);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      alert(error.response?.data?.message || 'Failed to update profile');
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await adminTeammateFinderAPI.deleteProfile(id);
      if (response.success) {
        setDeleteConfirm(null);
        fetchProfiles();
        queryClient.invalidateQueries(['adminTeammateFinder']);
      }
    } catch (error) {
      console.error('Error deleting profile:', error);
      alert(error.response?.data?.message || 'Failed to delete profile');
    }
  };

  return (
    <div style={{ background: 'white', borderRadius: '8px', padding: '2rem', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
          <div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#043663', marginBottom: '0.5rem' }}>
              Find Teammate Profiles Management
            </h2>
            <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
              Legacy anonymous bulletin (retired). Use this section only to clean up old rows. New listings use player accounts — Teams → Find players with profile public toggle.
            </p>
            <p style={{ color: '#b45309', fontSize: '0.8rem', marginTop: '0.5rem', fontWeight: 600 }}>
              Deprecated: players should use Profile settings → “Make my profile public” instead of posting here.
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.5rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer', userSelect: 'none' }}>
              <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>
                {isFeatureEnabled ? '✅ Enabled' : '❌ Disabled'}
              </span>
              <input
                type="checkbox"
                checked={isFeatureEnabled}
                onChange={(e) => handleToggleFeature(e.target.checked)}
                disabled={loadingSetting || savingSetting}
                style={{ width: '20px', height: '20px', cursor: savingSetting ? 'not-allowed' : 'pointer' }}
              />
            </label>
            <span style={{ fontSize: '0.75rem', color: '#6b7280', fontStyle: 'italic' }}>
              {isFeatureEnabled 
                ? 'Feature is visible on frontend' 
                : 'Feature is hidden on frontend (data preserved)'}
            </span>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        <select
          value={filterCanton}
          onChange={(e) => setFilterCanton(e.target.value)}
          style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px' }}
        >
          <option value="">All Cantons</option>
          {cantons.map(c => (
            <option key={c.code} value={c.code}>{c.name}</option>
          ))}
        </select>
        <select
          value={filterPublished}
          onChange={(e) => setFilterPublished(e.target.value)}
          style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px' }}
        >
          <option value="">All Status</option>
          <option value="true">Published</option>
          <option value="false">Unpublished</option>
        </select>
      </div>

      {loading ? (
        <div>Loading profiles...</div>
      ) : profiles.length === 0 ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
          No profiles found
        </div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f3f4f6', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600 }}>Name</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600 }}>Email</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600 }}>Phone</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600 }}>Canton</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600 }}>Skill Level</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600 }}>Status</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600 }}>Created</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {profiles.map((profile) => (
                <tr key={profile.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  {editingId === profile.id ? (
                    <>
                      <td style={{ padding: '0.75rem' }}>
                        <input
                          type="text"
                          value={editForm.firstName}
                          onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                          style={{ width: '100%', padding: '0.25rem', border: '1px solid #d1d5db', borderRadius: '4px' }}
                          placeholder="First Name"
                        />
                        <input
                          type="text"
                          value={editForm.lastName}
                          onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                          style={{ width: '100%', padding: '0.25rem', marginTop: '0.25rem', border: '1px solid #d1d5db', borderRadius: '4px' }}
                          placeholder="Last Name"
                        />
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        <input
                          type="email"
                          value={editForm.email}
                          onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                          style={{ width: '100%', padding: '0.25rem', border: '1px solid #d1d5db', borderRadius: '4px' }}
                        />
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        <input
                          type="text"
                          value={editForm.phoneNumber}
                          onChange={(e) => setEditForm({ ...editForm, phoneNumber: e.target.value })}
                          style={{ width: '100%', padding: '0.25rem', border: '1px solid #d1d5db', borderRadius: '4px' }}
                        />
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        <select
                          value={editForm.canton}
                          onChange={(e) => setEditForm({ ...editForm, canton: e.target.value })}
                          style={{ width: '100%', padding: '0.25rem', border: '1px solid #d1d5db', borderRadius: '4px' }}
                        >
                          <option value="">Select Canton</option>
                          {cantons.map(c => (
                            <option key={c.code} value={c.code}>{c.name}</option>
                          ))}
                        </select>
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        <select
                          value={editForm.skillLevel}
                          onChange={(e) => setEditForm({ ...editForm, skillLevel: e.target.value })}
                          style={{ width: '100%', padding: '0.25rem', border: '1px solid #d1d5db', borderRadius: '4px' }}
                        >
                          <option value="">No Skill Level</option>
                          {skillLevels.map(sl => (
                            <option key={sl.value} value={sl.value}>{sl.label}</option>
                          ))}
                        </select>
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        <select
                          value={editForm.isPublished ? 'true' : 'false'}
                          onChange={(e) => setEditForm({ ...editForm, isPublished: e.target.value === 'true' })}
                          style={{ width: '100%', padding: '0.25rem', border: '1px solid #d1d5db', borderRadius: '4px' }}
                        >
                          <option value="true">Published</option>
                          <option value="false">Unpublished</option>
                        </select>
                      </td>
                      <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#6b7280' }}>
                        {new Date(profile.created_at).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        <button
                          onClick={handleSaveEdit}
                          style={{
                            padding: '0.25rem 0.75rem',
                            background: '#043663',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            marginRight: '0.5rem'
                          }}
                        >
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setEditingId(null);
                            setEditForm({});
                          }}
                          style={{
                            padding: '0.25rem 0.75rem',
                            background: '#6b7280',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          Cancel
                        </button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td style={{ padding: '0.75rem', fontWeight: 500 }}>
                        {profile.first_name} {profile.last_name}
                      </td>
                      <td style={{ padding: '0.75rem' }}>{profile.email}</td>
                      <td style={{ padding: '0.75rem' }}>{profile.phone_number}</td>
                      <td style={{ padding: '0.75rem' }}>
                        {cantons.find(c => c.code === profile.canton)?.name || profile.canton}
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        {profile.skill_level ? (
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            background: '#e0e7ff',
                            color: '#4338ca',
                            borderRadius: '4px',
                            fontSize: '0.875rem'
                          }}>
                            {profile.skill_level}
                          </span>
                        ) : (
                          <span style={{ color: '#9ca3af' }}>N/A</span>
                        )}
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        <span style={{
                          padding: '0.25rem 0.5rem',
                          background: profile.is_published ? '#d1fae5' : '#fee2e2',
                          color: profile.is_published ? '#065f46' : '#991b1b',
                          borderRadius: '4px',
                          fontSize: '0.875rem',
                          fontWeight: 500
                        }}>
                          {profile.is_published ? 'Published' : 'Unpublished'}
                        </span>
                      </td>
                      <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: '#6b7280' }}>
                        {new Date(profile.created_at).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        <button
                          onClick={() => handleEdit(profile)}
                          style={{
                            padding: '0.25rem 0.75rem',
                            background: '#043663',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            marginRight: '0.5rem',
                            fontSize: '0.875rem'
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(profile.id)}
                          style={{
                            padding: '0.25rem 0.75rem',
                            background: '#dc2626',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.875rem'
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {deleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '8px',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h3 style={{ marginBottom: '1rem' }}>Confirm Delete</h3>
            <p style={{ marginBottom: '1.5rem', color: '#6b7280' }}>
              Are you sure you want to delete this profile? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setDeleteConfirm(null)}
                style={{
                  padding: '0.5rem 1rem',
                  background: '#e5e7eb',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                style={{
                  padding: '0.5rem 1rem',
                  background: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTeammateFinderSection;

